/**
 * MAHI — Profile. Everything optional lives here, editable any time, in any
 * order: email (+ verification), the try-on photo, style preferences, and
 * basic info. Nothing on this page blocks anything else in the app.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Check, ChevronLeft, Loader2, Mail, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import mira, { errMsg, trackEvent } from '../api';
import { useMiraAuth } from '../auth';
import MiraShell from '../components/MiraShell';

const STYLE_OPTIONS = ['Traditional', 'Modern', 'Office Wear', 'Casual', 'Party', 'Ethnic', 'Fusion', 'Vacation'];
const COLORS = ['Black', 'White', 'Red', 'Blue', 'Green', 'Pink', 'Champagne', 'Pastel'];
const FABRICS = ['Silk', 'Cotton', 'Linen', 'Chiffon', 'Georgette', 'Tulle', 'Velvet'];
const OCCASIONS = ['Wedding', 'Office', 'Dinner', 'Festive', 'Vacation', 'Casual day'];

const Chip = ({ active, children, onClick }) => (
  <button
    type="button" onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition border ${
      active
        ? 'bg-slate-900 text-white border-slate-900'
        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
    }`}
  >{children}</button>
);

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.15)] p-6 mb-5">
    <h3 className="text-base font-semibold text-slate-900">{title}</h3>
    {subtitle && <p className="text-xs text-slate-500 mt-0.5 mb-4">{subtitle}</p>}
    {!subtitle && <div className="mb-4" />}
    {children}
  </div>
);

export default function MiraProfile() {
  const navigate = useNavigate();
  const { user, refresh } = useMiraAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [completeness, setCompleteness] = useState(null);

  const [name, setName] = useState(user?.name || '');
  const [gender, setGender] = useState(user?.gender || '');

  const [email, setEmail] = useState(user?.email || '');
  const [emailCode, setEmailCode] = useState('');
  const [showEmailCode, setShowEmailCode] = useState(false);

  const [prefs, setPrefs] = useState({
    favorite_colors: [], favorite_fabrics: [], occasions: [], styles: [],
  });

  useEffect(() => { document.title = 'MAHI — Profile'; }, []);

  const loadCompleteness = () =>
    mira.get('/profile/completeness').then((r) => setCompleteness(r.data)).catch(() => {});

  useEffect(() => {
    loadCompleteness();
    mira.get('/profile/preferences').then((r) => {
      const p = r.data?.preferences || {};
      setPrefs({
        favorite_colors: p.favorite_colors || [],
        favorite_fabrics: p.favorite_fabrics || [],
        occasions: p.occasions || [],
        styles: p.styles || [],
      });
    }).catch(() => {});
  }, []);

  const toggle = (key, v) => setPrefs((p) => ({
    ...p,
    [key]: p[key].includes(v) ? p[key].filter((x) => x !== v) : [...p[key], v],
  }));

  const saveBasic = async () => {
    if (!name.trim()) return toast.error('Enter a name');
    if (!gender) return toast.error('Pick a gender');
    setBusy(true);
    try {
      await mira.patch('/profile/basic', { name: name.trim(), gender });
      trackEvent('profile_basic_saved', { context: 'profile_page' });
      await refresh();
      toast.success('Saved');
    } catch (e) { toast.error(errMsg(e, 'Could not save')); }
    finally { setBusy(false); }
  };

  const saveEmail = async () => {
    if (!email.trim()) return toast.error('Enter an email');
    setBusy(true);
    try {
      await mira.patch('/profile/email', { email: email.trim() });
      trackEvent('profile_email_saved');
      await refresh(); loadCompleteness();
      setShowEmailCode(true);
      toast.success('Check your inbox for a verification code');
    } catch (e) { toast.error(errMsg(e, 'Could not save email')); }
    finally { setBusy(false); }
  };

  const verifyEmail = async () => {
    if (!emailCode.trim()) return toast.error('Enter the code');
    setBusy(true);
    try {
      await mira.post('/profile/email/verify', { code: emailCode.trim() });
      trackEvent('profile_email_verified');
      await refresh();
      setShowEmailCode(false); setEmailCode('');
      toast.success('Email verified');
    } catch (e) { toast.error(errMsg(e, 'Invalid code')); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Please choose an image');
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await mira.post('/profile/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      trackEvent('profile_photo_uploaded');
      await refresh(); loadCompleteness();
      toast.success('Looking great');
    } catch (e) { toast.error(errMsg(e, 'Upload failed')); }
    finally { setBusy(false); }
  };

  const savePreferences = async () => {
    setBusy(true);
    try {
      await mira.post('/profile/preferences', prefs);
      trackEvent('profile_preferences_saved');
      loadCompleteness();
      toast.success('Saved');
    } catch (e) { toast.error(errMsg(e, 'Could not save')); }
    finally { setBusy(false); }
  };

  return (
    <MiraShell>
      <div className="min-h-screen px-5 py-8 max-w-lg mx-auto">
        <button onClick={() => navigate('/mira/home')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-6">
          <ChevronLeft className="w-4 h-4" /> Back to Home
        </button>

        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Your profile</h1>
        <p className="text-sm text-slate-500 mb-2">Everything here is optional — save what you want, whenever you want.</p>

        {completeness && completeness.percent < 100 && (
          <div className="mb-6 mt-4 rounded-2xl bg-slate-50 ring-1 ring-slate-100 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white ring-1 ring-slate-200 flex items-center justify-center text-xs font-semibold text-slate-700 flex-none">
              {completeness.percent}%
            </div>
            <p className="text-xs text-slate-500">Profile complete — the rest just helps MAHI style you better.</p>
          </div>
        )}
        {!completeness && <div className="mb-6" />}

        <Card title="Basic info">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-300" />
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[{ value: 'female', label: 'Female' }, { value: 'male', label: 'Male' }, { value: 'other', label: 'Other' }].map((g) => (
              <button key={g.value} type="button" onClick={() => setGender(g.value)}
                className={`rounded-xl py-2 text-sm font-medium border transition ${
                  gender === g.value
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                }`}
              >{g.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-500 mb-3">
            <span>Mobile</span>
            <span className="font-medium text-slate-700">+{user?.phone}</span>
          </div>
          <button onClick={saveBasic} disabled={busy} className="text-sm font-medium text-slate-900 hover:text-cyan-700 disabled:opacity-50">
            Save
          </button>
        </Card>

        <Card title="Photo" subtitle="Used to generate your try-ons.">
          <div className="flex items-center gap-4">
            <div onClick={() => fileRef.current?.click()}
                 className="cursor-pointer w-20 h-20 rounded-2xl bg-slate-50 ring-1 ring-slate-100 flex items-center justify-center overflow-hidden relative flex-none">
              {user?.picture
                ? <img src={user.picture} alt="" className="absolute inset-0 w-full h-full object-cover" />
                : <Camera className="w-6 h-6 text-slate-300" />}
              {busy && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-500" /></div>}
            </div>
            <div>
              <button onClick={() => fileRef.current?.click()} className="text-sm text-slate-700 underline">
                {user?.picture ? 'Change photo' : 'Upload photo'}
              </button>
              <p className="text-xs text-slate-400 mt-1">Full-length, good lighting, front-facing.</p>
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => uploadPhoto(e.target.files?.[0])} />
        </Card>

        <Card title="Email" subtitle="Optional — get your try-on results by mail.">
          <div className="relative mb-3">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-300"
            />
            {user?.email_verified && user?.email === email && (
              <ShieldCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
            )}
          </div>
          {!showEmailCode ? (
            <button onClick={saveEmail} disabled={busy} className="text-sm font-medium text-slate-900 hover:text-cyan-700 disabled:opacity-50">
              {user?.email_verified && user?.email === email ? 'Verified' : 'Save & verify'}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <input value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="Code from email"
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-cyan-200" />
              <button onClick={verifyEmail} disabled={busy} className="rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-medium disabled:opacity-50">
                Verify
              </button>
            </div>
          )}
        </Card>

        <Card title="Style" subtitle="Pick anything that resonates.">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Style</div>
          <div className="flex flex-wrap gap-2 mb-4">
            {STYLE_OPTIONS.map((s) => (
              <Chip key={s} active={prefs.styles.includes(s)} onClick={() => toggle('styles', s)}>{s}</Chip>
            ))}
          </div>
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Colors</div>
          <div className="flex flex-wrap gap-2 mb-4">
            {COLORS.map((c) => (
              <Chip key={c} active={prefs.favorite_colors.includes(c)} onClick={() => toggle('favorite_colors', c)}>{c}</Chip>
            ))}
          </div>
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Fabrics</div>
          <div className="flex flex-wrap gap-2 mb-4">
            {FABRICS.map((f) => (
              <Chip key={f} active={prefs.favorite_fabrics.includes(f)} onClick={() => toggle('favorite_fabrics', f)}>{f}</Chip>
            ))}
          </div>
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Occasions</div>
          <div className="flex flex-wrap gap-2 mb-5">
            {OCCASIONS.map((o) => (
              <Chip key={o} active={prefs.occasions.includes(o)} onClick={() => toggle('occasions', o)}>{o}</Chip>
            ))}
          </div>
          <button onClick={savePreferences} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Save preferences</>}
          </button>
        </Card>
      </div>
    </MiraShell>
  );
}
